import { Box, Container, Grid, Hidden, Paper, Typography } from '@material-ui/core'
import { format, subDays } from 'date-fns'
import isEmpty from 'lodash/isEmpty'
import React, { Component } from 'react'
import scriptLoader from 'react-async-script-loader'
import { connect } from 'react-redux'

import { api, API_MONO } from '../api/api'
import ComingSoon from '../components/coming-soon/ComingSoon'
import OrderStatus from '../components/common/OrderStatus'
import StickyContainer from '../components/common/StickyContainer'
import Delivery from '../components/home/Delivery'
import FeedbackLink from '../components/home/FeedbackLink'
import HomeDialogs from '../components/home/HomeDialogs'
import HomeSidebar from '../components/home/HomeSidebar'
import HomeSkeletonLoading from '../components/home/HomeSkeletonLoading'
import HomeSnackbars from '../components/home/HomeSnackbars'
import NoDeliveriesToday from '../components/home/NoDeliveriesToday'
import { YourLocation } from '../components/home/YourLocation'
import { MAPS_SCRIPT_URL } from '../components/location-search/LocationSearchBox'
import NewPageLayout from '../layouts/NewPageLayout'
import { loadDeliveryScheduleStart, selectDelivery } from '../redux/modules/delivery'
import { addFavoritesForMenusStart, getFavoritesForMenusCount } from '../redux/modules/favorite'
import { changeSelectedDay } from '../redux/modules/home'
import { loadLocationStart } from '../redux/modules/location'
import { loadOrderStart } from '../redux/modules/orderDetail'
import {
  isSelectedDeliveryDateActive,
  selectCurrentLocationWithAddress,
  selectCurrentSavedLocation,
  selectLocationErrorCode,
} from '../redux/modules/selectors'
import { profileRoute } from '../routes/routes'
import { getDayName } from '../util/dateUtils'
import { pushAnalyticsEvent } from '../util/gtmUtils'
import {
  pushSnowplow,
  toDeliveryEntities,
  toOrderEntities,
  toOrderProcessSchema,
} from '../util/snowplow'

const LoadDayError = () => (
  <div className="center-xs">
    <h5 className="error center-xs">
      <i className="icon-warning" /> Oops! Something went wrong.
    </h5>
    <p>
      Try refreshing the page or switching between days. If this problem persists, please{' '}
      <a href={`/contact`}>contact support</a>
    </p>
  </div>
)

class Home extends Component {
  state = {
    locationStatus: null,
  }

  async componentDidMount() {
    let {
      match: {
        params: { locationId },
      },
      loadLocationStart,
    } = this.props
    const { isAuthenticated, location } = this.props
    locationId = Number(locationId)
    //this loads the location in case the user isn't authenticated (it will already be loaded otherwise)
    //... important for some logic to check if the user needs to login or not
    if (Number(location?.deliveryLocationId) !== locationId) {
      loadLocationStart(locationId)
    }

    // TEMPORARY for the paused locations modal due to COVID-19
    // Fetch the status (suspended / not suspended) of the location before making other calls
    try {
      const locationStatus = await api.get(API_MONO + `/location/${locationId}/status`)
      this.setState({ locationStatus })
    } catch (err) {
      // Don't do anything with the error as we don't want this to interrupt with rest of the data loading
      console.error(err)
    }

    this.fetchDeliveries(locationId)
    if (isAuthenticated) {
      this.fetchPageData(locationId)
    }
    pushAnalyticsEvent('Ordering', 'View Deliveries', undefined, locationId)
  }

  componentDidUpdate(prevProps) {
    const { isAuthenticated, loadLocationStart } = this.props
    let {
      deliveryError,
      match: {
        params: { locationId = 0 },
      },
    } = this.props
    locationId = Number(locationId)
    if (Number(prevProps.match.params.locationId) !== locationId) {
      loadLocationStart(locationId)
      this.fetchDeliveries(locationId)
      this.fetchPageData(locationId, true)
    }
    if (prevProps.isAuthenticated === false && isAuthenticated === true) {
      this.fetchPageData(locationId)
    }
    if (deliveryError) {
      pushSnowplow(toOrderProcessSchema('daily', 'deliveries_view', undefined, deliveryError))
    }
    if (this.props.deliveries && this.props.deliveries !== prevProps.deliveries) {
      this.fetchFavoritesForMenus()
      this.pushSnowplowEvent()
    }
  }

  pushSnowplowEvent = () => {
    const { deliveries, orders, selectedDate } = this.props
    const localEntities = isEmpty(orders)
      ? toDeliveryEntities(deliveries)
      : [...toDeliveryEntities(deliveries), ...toOrderEntities(orders)]
    const onDate = format(subDays(selectedDate, 0), 'YYYY-MM-DD')

    pushSnowplow(toOrderProcessSchema('daily', 'deliveries_view', onDate), localEntities)
  }

  fetchDeliveries = locationId => {
    const { loadDeliveryScheduleStart, selectedDate } = this.props
    loadDeliveryScheduleStart(locationId, selectedDate)
  }

  fetchFavoritesForMenus = async () => {
    const { addFavoritesForMenusStart, deliveries, isAuthenticated } = this.props
    const menuIds = deliveries.map(d => d.menuId)
    if (menuIds.length > 0 && isAuthenticated) {
      const result = await getFavoritesForMenusCount(menuIds)
      addFavoritesForMenusStart(result)
    }
  }

  fetchPageData = currentLocationId => {
    const { isAuthenticated, loadOrderStart, selectedDate } = this.props
    const locationId = currentLocationId || Number(this.props.match.params.locationId)
    if (isAuthenticated) {
      loadOrderStart(selectedDate, locationId)
    }
  }

  render() {
    const {
      activationDate,
      changeSelectedDay,
      currentSavedLocation,
      deliveries,
      deliveriesWithMealTime,
      deliveryError,
      isAuthenticated,
      isLoadingDeliveries,
      isScriptLoadSucceed,
      isScriptLoaded,
      isSelectedDayActive,
      isWebpSupported,
      loadingNewDay,
      location,
      locationErrorCode,
      orders,
      selectedDate,
    } = this.props
    let {
      friendHasOrdered,
      match: {
        params: { locationId },
      },
    } = this.props
    const { locationStatus } = this.state
    locationId = Number(locationId)
    const hasOrders = orders && orders.length > 0
    const showModalPausedLocation = Boolean(locationStatus?.suspended)

    if (Number(locationErrorCode) === 404 && !showModalPausedLocation) {
      window.location.href = profileRoute.path
    }

    return (
      <NewPageLayout>
        <>
          {loadingNewDay || isLoadingDeliveries ? (
            <HomeSkeletonLoading />
          ) : (
            <Grid container>
              <Container maxWidth="lg">
                <Grid item md={9} xs={12}>
                  <Grid container justify="center">
                    <Grid item md={11} xs={12}>
                      <Box marginBottom={3}>
                        <YourLocation
                          changeSelectedDay={changeSelectedDay}
                          isScriptLoaded={isScriptLoaded}
                          isScriptLoadSucceed={isScriptLoadSucceed}
                          locationId={locationId}
                          name={
                            currentSavedLocation?.name ||
                            currentSavedLocation?.deliveryLocationName ||
                            location?.deliveryLocationName
                          }
                          orderCount={orders?.length}
                          selectedDate={selectedDate}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Container>
              <Grid item xs={12}>
                {/* Show manage order card */}
                {hasOrders && (
                  <Box marginBottom="48px">
                    <Paper color="secondary" square variant="outlined">
                      <Box paddingY={4}>
                        <Container className="container">
                          <Grid container justify="center">
                            <Grid item md={8} xs={12}>
                              <OrderStatus locationId={locationId} />
                            </Grid>
                          </Grid>
                        </Container>
                      </Box>
                    </Paper>
                  </Box>
                )}
              </Grid>
              <Container maxWidth="lg">
                <Grid container>
                  <Grid item md={9} xs={12}>
                    <Grid container justify="center">
                      <Grid item md={11} xs={12}>
                        <>
                          {deliveryError && <LoadDayError />}
                          {/* If location had deliveries show delivery cards*/}
                          {deliveries?.length > 0 && (
                            <>
                              {hasOrders && (
                                <Typography component="h2" gutterBottom variant="h6">
                                  More options delivering {getDayName(selectedDate)}
                                </Typography>
                              )}
                              {deliveriesWithMealTime.map(dWithMealTime => (
                                <Box key={dWithMealTime.subtitleText} paddingY={8}>
                                  {/* <Typography component="p" gutterBottom variant="subtitle1">
                              {dWithMealTime.subtitleText}
                            </Typography> */}
                                  <Delivery
                                    deliveries={dWithMealTime.deliveries}
                                    friendHasOrdered={friendHasOrdered}
                                    isSelectedDayActive={isSelectedDayActive}
                                    isWebpSupported={isWebpSupported}
                                    locationId={locationId}
                                    selectDelivery={selectDelivery}
                                    selectedDate={selectedDate}
                                  />
                                </Box>
                              ))}
                            </>
                          )}
                          {/* If location has no deliveries and is not a new building show no deliveries component */}
                          {deliveries?.length === 0 && isSelectedDayActive && (
                            <NoDeliveriesToday selectedDate={selectedDate} />
                          )}
                        </>

                        {/* Load coming soon content for new buildings */}
                        {!isSelectedDayActive && activationDate && (
                          <ComingSoon
                            activationDate={activationDate}
                            isAuthenticated={isAuthenticated}
                            locationId={locationId}
                            onChangeDay={date => changeSelectedDay(date, locationId)}
                          />
                        )}
                        <Box className="center-xs" marginTop={4}>
                          <FeedbackLink />
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                  {/* Sidebar */}
                  {isAuthenticated && (
                    <Hidden smDown>
                      <Grid item md={3} xs={12}>
                        <StickyContainer stickyStyle={{ paddingTop: '50px' }} topOffset={-80}>
                          <HomeSidebar
                            showSubscription={
                              !hasOrders && isSelectedDayActive && deliveries?.length > 0
                            }
                          />
                        </StickyContainer>
                      </Grid>
                    </Hidden>
                  )}
                </Grid>
              </Container>
            </Grid>
          )}
        </>
        <HomeDialogs
          locationStatus={locationStatus}
          isScriptLoaded={isScriptLoaded}
          isScriptLoadSucceed={isScriptLoadSucceed}
        />
        <HomeSnackbars />
      </NewPageLayout>
    )
  }
}

const mapStateToProps = state => {
  const { deliveries, deliveriesWithMealTime, isLoadingDeliveries } = state.delivery
  const deliveryError = state.delivery.error
  const { loadingNewDay, selectedDate } = state.home
  const { isAuthenticated } = state.user
  const location = selectCurrentLocationWithAddress(state)
  const { location: { activationDate } = {} } = state.location
  const locationErrorCode = selectLocationErrorCode(state)
  const isSelectedDayActive = isSelectedDeliveryDateActive(state)
  const { loading, orders } = state.orderDetail
  const { isWebpSupported } = state.browser
  const currentSavedLocation = selectCurrentSavedLocation(state)
  return {
    activationDate,
    currentSavedLocation,
    deliveries,
    deliveriesWithMealTime,
    deliveryError,
    isAuthenticated,
    isLoadingDeliveries,
    isOrdersLoading: loading,
    isSelectedDayActive,
    isWebpSupported,
    loadingNewDay,
    location,
    locationErrorCode,
    orders,
    selectedDate,
  }
}

const mapDispatchToProps = {
  addFavoritesForMenusStart,
  changeSelectedDay,
  loadDeliveryScheduleStart,
  loadLocationStart,
  loadOrderStart,
  selectDelivery,
}

export default scriptLoader(MAPS_SCRIPT_URL)(connect(mapStateToProps, mapDispatchToProps)(Home))
